import React, { useState, useEffect, useMemo } from 'react';
import { Combobox, TextInput, Loader, Text, Group, Stack, useCombobox } from '@mantine/core';
import { useQuery } from '@apollo/client';
import debounce from 'lodash.debounce';
import { useLocation } from 'react-router-dom';

import { GET_CUSTOMERS_QUERY, GET_CUSTOMERS_BY_ACCTNO_QUERY } from '../../queries';

const CustomerSelect = ({ onCustomerSelect }) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const defaultAccountNumber = queryParams.get('acctno');

  // Debounce the search input
  const debouncedChangeHandler = useMemo(
    () => debounce((value) => {
      setDebouncedSearch(value);
    }, 300),
    []
  );

  const handleInputChange = (value) => {
    setInputValue(value);
    debouncedChangeHandler(value);
  };

  // Query for customers by name using Apollo Client's useQuery
  const { data: nameData, loading: nameLoading, error: nameError } = useQuery(
    GET_CUSTOMERS_QUERY,
    {
      variables: { search: debouncedSearch },
      skip: debouncedSearch.length < 2,
    }
  );

  // Query for customer by account number (if provided) using Apollo Client's useQuery
  const { data: acctNoData, loading: acctNoLoading, error: acctNoError } = useQuery(
    GET_CUSTOMERS_BY_ACCTNO_QUERY,
    {
      variables: { acctNo: defaultAccountNumber },
      skip: !defaultAccountNumber,
    }
  );

  // Handle errors
  useEffect(() => {
    if (nameError) console.error('Error fetching customers by name:', nameError);
    if (acctNoError) console.error('Error fetching customer by account number:', acctNoError);
  }, [nameError, acctNoError]);

  // Update options based on search results
  useEffect(() => {
    if (nameData?.customer?.nodes) {
      setOptions(nameData.customer.nodes);
    }
  }, [nameData]);

  // Handle default customer from account number query
  useEffect(() => {
    if (defaultAccountNumber && acctNoData?.customer?.nodes?.length > 0 && !selectedCustomer) {
      const foundCustomer = acctNoData.customer.nodes[0];
      setSelectedCustomer(foundCustomer);
      setInputValue(foundCustomer.name);
      onCustomerSelect(foundCustomer);
      setOptions(prevOptions => {
        if (!prevOptions.some(c => c.acctNo === foundCustomer.acctNo)) {
          return [foundCustomer, ...prevOptions];
        }
        return prevOptions;
      });
    }
  }, [defaultAccountNumber, acctNoData, onCustomerSelect, selectedCustomer]);

  const handleCustomerSelect = (value) => {
    const customer = options.find(option => option.acctNo === value);
    setSelectedCustomer(customer);
    if (customer) {
      setInputValue(customer.name);
      onCustomerSelect(customer);
    } else {
      setInputValue('');
      onCustomerSelect(null);
    }
    combobox.closeDropdown();
  };

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleCustomerSelect}
      withinPortal={false}
    >
      <Combobox.Target>
        <TextInput
          label="Select Customer"
          placeholder="Search customers..."
          value={inputValue}
          onChange={(event) => handleInputChange(event.currentTarget.value)}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={
            (nameLoading || acctNoLoading) ? (
              <Loader size="xs" />
            ) : (
              <Combobox.Chevron />
            )
          }
          styles={{
            root: { minWidth: 400 },
            label: { marginBottom: 2 },
            input: { fontSize: 14 }
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length === 0 && debouncedSearch.length >= 2 && !nameLoading ? (
            <Combobox.Empty>No customers found</Combobox.Empty>
          ) : (
            options.map((option) => (
              <Combobox.Option 
                value={option.acctNo} 
                key={option.acctNo}
                py={4}
                px="xs"
              >
                <Stack gap={0}>
                  <Text fw={600} size="sm">
                    {option.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {option.acctNo} - {option.assignedSalesperson}
                  </Text>
                </Stack>
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default CustomerSelect;